import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import RootStore from '@App/stores/root-store'
import { OverclaimViewModel } from '@Entities/overclaim/overclaim-view'
import * as overclaimAPI from '../../api/routes/overclaim'
import { OverclaimRelinquishment } from '@Entities/overclaim/overclaim-relinquishment'
import { OverclaimHistory } from '@Entities/overclaim/overclaim-history'
import {
    OverclaimUploadedDocument,
    OverclaimUploadedDocumentsForPublisher,
} from '@Entities/overclaim/overclaim-uploaded-document'
import { getAllOverclaimUploadedDocumentsFor } from '@App/api/routes/overclaim'
import { cloneDeep } from 'lodash'
import { amendOverclaimShares } from '../../api/routes/overclaim'

export default class OverclaimViewStore {
    private _rootStore: RootStore

    private _overclaimViewItem: OverclaimViewModel | undefined
    private _overclaimHistory: OverclaimHistory[] | undefined
    private _relinquishments: OverclaimRelinquishment[] | undefined
    private _overclaimUploadedDocuments: OverclaimUploadedDocumentsForPublisher | undefined

    constructor(rootStore: RootStore) {
        makeObservable<
            OverclaimViewStore,
            '_overclaimViewItem' | '_overclaimHistory' | '_relinquishments' | '_overclaimUploadedDocuments'
        >(this, {
            _overclaimViewItem: observable,
            _overclaimHistory: observable,
            _relinquishments: observable,
            _overclaimUploadedDocuments: observable,
            isExistingRelinquishment: computed,
            getOverclaimViewItemByHeaderId: action,
            destroyOverclaimViewItem: action,
            setOverclaimViewItem: action,
        })
        this._rootStore = rootStore
    }

    async getOverclaimViewItemByHeaderId(overclaimHeaderId: number, rightsholderId: number): Promise<void> {
        const response = await overclaimAPI.getOverclaimViewItemByOverclaimHeaderId(overclaimHeaderId, rightsholderId)

        if (response.data) {
            runInAction(() => {
                const newOverclaimItem = response.data
                newOverclaimItem.workType = 'overclaim'
                this._overclaimViewItem = newOverclaimItem
                this.getRelinquishmentsForCurrentOverclaimViewItem()
                this.getOverclaimHistoryByHeaderId()
            })
        }
    }

    async getOverclaimHistoryByHeaderId(): Promise<void> {
        const overclaimHeaderId = this._overclaimViewItem?.overclaimHeaderId
        const ipId = this._rootStore.rightsholderStore.currentRightsholderId
        if (overclaimHeaderId) {
            try {
                const response = await overclaimAPI.getOverclaimHistoryByOverclaimHeaderId(overclaimHeaderId, ipId)
                runInAction(() => {
                    this._overclaimHistory = response.data
                })
            } catch {
                this._rootStore.popupStore.setAlertData(
                    `There has been an issue updating overclaim history.
                 Please reach out to our Support Team via the Contact Us page.`,
                    true
                )
            }
        }
    }

    async amendOverclaim(): Promise<void> {
        if (this._overclaimViewItem && this._overclaimViewItem.overclaimHeaderId) {
            const overclaimViewItemCopy: OverclaimViewModel = cloneDeep(this._overclaimViewItem)
            const ipId = this._rootStore.rightsholderStore.currentRightsholderId
            overclaimViewItemCopy.originalPublishers = overclaimViewItemCopy.originalPublishers.filter(
                (publisher) => publisher.topMemberIpId === ipId
            )

            try {
                await amendOverclaimShares(this._overclaimViewItem.overclaimHeaderId, ipId, overclaimViewItemCopy).then(
                    () => {
                        this.getOverclaimHistoryByHeaderId()
                    }
                )
            } catch {
                this._rootStore.popupStore.setAlertData(
                    `This amendment is unable to be submitted at this time.
                 Please reach out to our Support Team via the Contact Us page.`,
                    true
                )
            }
        }
    }

    async sendOverclaimEmail(content: string): Promise<void> {
        if (this.overclaimViewItem?.overclaimHeaderId) {
            this._rootStore.preloaderStore.openTransparentPreloader(true)
            try {
                await overclaimAPI
                    .sendEmail(
                        this.overclaimViewItem?.overclaimHeaderId,
                        this._rootStore.rightsholderStore.currentRightsholderId,
                        content
                    )
                    .then(() => this.getOverclaimHistoryByHeaderId())
            } catch {
                this._rootStore.popupStore.setAlertData(
                    `Your email is unable to be sent at this time.
                 Please reach out to our Support Team via the Contact Us page.`,
                    true
                )
            } finally {
                this._rootStore.preloaderStore.openTransparentPreloader(false)
            }
        }
    }

    async createOverclaimRelinquishment(): Promise<void> {
        if (this._overclaimViewItem?.overclaimHeaderId) {
            await overclaimAPI
                .createOverclaimRelinquishment(
                    this._rootStore.rightsholderStore.currentRightsholderId,
                    this._overclaimViewItem?.overclaimHeaderId
                )
                .then(() => {
                    this.getRelinquishmentsForCurrentOverclaimViewItem()
                    this.getOverclaimHistoryByHeaderId()
                })
                .catch(() => Promise.reject())
        } else {
            return Promise.reject()
        }
    }

    async getRelinquishmentsForCurrentOverclaimViewItem(): Promise<void> {
        if (this._overclaimViewItem?.overclaimHeaderId) {
            const relinquishments = await overclaimAPI.getRelinquishmentsForOverclaim(
                this._rootStore.rightsholderStore.currentRightsholderId,
                this._overclaimViewItem.overclaimHeaderId
            )
            runInAction(() => {
                this._relinquishments = relinquishments.data ?? []
            })
        }
    }

    async loadOverclaimDocumentsForCurrentOverclaimViewItem(): Promise<void> {
        if (this._overclaimViewItem?.overclaimHeaderId) {
            const documentResponse = await getAllOverclaimUploadedDocumentsFor(
                this._rootStore.rightsholderStore.currentRightsholderId,
                this._overclaimViewItem.overclaimHeaderId
            )
            runInAction(() => {
                this._overclaimUploadedDocuments = documentResponse.data
            })
        }
    }

    async updateOverclaimDocumentDescription(document: OverclaimUploadedDocument, description: string) {
        if (this._overclaimViewItem?.overclaimHeaderId) {
            const updatedDocResponse = await overclaimAPI.updateOverclaimDocumentDescription(
                this._rootStore.rightsholderStore.currentRightsholderId,
                this._overclaimViewItem.overclaimHeaderId,
                document.memberUploadId,
                description
            )

            runInAction(() => {
                const updatedDoc = updatedDocResponse.data
                if (this._overclaimUploadedDocuments) {
                    for (const [, documents] of Object.entries(this._overclaimUploadedDocuments)) {
                        const index = documents.findIndex((doc) => doc.id === document.id)
                        if (index !== -1) {
                            documents[index] = updatedDoc
                            this._overclaimUploadedDocuments = { ...this._overclaimUploadedDocuments } // self-assign to trigger MobX (properties of object are not observable)
                            break
                        }
                    }
                }
            })
        }
    }

    get isExistingRelinquishment() {
        return !!this._relinquishments?.some(
            (relinquishment) => relinquishment.ipId === this._rootStore.rightsholderStore.currentRightsholderId
        )
    }

    get overclaimViewItem(): OverclaimViewModel | undefined {
        return this._overclaimViewItem
    }

    setOverclaimViewItem(data: OverclaimViewModel | undefined): void {
        this._overclaimViewItem = data
    }

    get overclaimHistory(): OverclaimHistory[] | undefined {
        return this._overclaimHistory
    }

    get overclaimDocuments(): OverclaimUploadedDocumentsForPublisher {
        return this._overclaimUploadedDocuments || {}
    }

    destroyOverclaimViewItem(): void {
        this._overclaimViewItem = undefined
    }
}
